import { apiStatusCodes } from '../config/global-variables';
import RefreshService from '../service/refresh.service';
import apiResponseTimeService from '../service/responseTime.service'
import loginModel, {clearCredentials} from './login.model';
import navigationModel from "./navigation/navigation.model";
import getRequestURL from '../utils/getRequestURL';

import { v4 as uuidv4 } from 'uuid';

let xhrMap = {};
let uniqueXhrMap = {};

/**
 * Base request function
 * @param {*} path                      - The relative URL
 * @param {*} params                    - The options for m.request(options)
 * @param {*} authenticationRequired    - Whether to include Authorization header. Default: true
 * @param {*} abortable                 - Whether to abort previous request before sending a new request. Default: false
 * @returns Promise
 */

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
    failedQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve());
    failedQueue = [];
};

/**
 * Retry a request with a raw m.request — bypasses 401/refresh logic so a
 * consistently-failing API cannot trigger another refresh cycle.
 */
function retryRaw(path, params = {}, authenticationRequired = true, abortable = false) {
    params ||= {};

    if (authenticationRequired !== false && loginModel.isAuthenticated() === false) {
        return Promise.reject({ code: apiStatusCodes.unauthorized, message: "Login required" })
    }

    const url = path.split('?')[0];
    const newGuid = uuidv4();

    if (abortable === true) {
        abortRequest(uniqueXhrMap[url]);
        uniqueXhrMap[url] = newGuid;
    }

    params.config = function (xhr) {
        xhrMap[newGuid] = xhr;
        apiResponseTimeService.addXhr(newGuid, xhr);
    };

    let options = Object.assign({
        url: getRequestURL(path),
        headers: {},
        withCredentials: true
    }, params);

    if (navigationModel.getCurrentMenuID()) {
        options.headers['From'] = navigationModel.getCurrentMenuID();
    }

    return m.request(options).finally(() => {
        delete xhrMap[newGuid];
        apiResponseTimeService.process(newGuid);
        if (abortable) {
            delete uniqueXhrMap[newGuid];
        }
    });
}


function request(path, params = {}, authenticationRequired = true, abortable = false) {
    params ||= {};

    // Only reject when authentication is explicitly required AND session is
    // confirmed invalid. When isAuthenticated() returns undefined (session check
    // in progress), allow the request through so the server can validate via cookie.
    if (authenticationRequired !== false && loginModel.isAuthenticated() === false) {
        return Promise.reject({ code: apiStatusCodes.unauthorized, message: "Login required" })
    }

    const url = path.split('?')[0];
    const newGuid = uuidv4();

    if (abortable === true) {
        abortRequest(uniqueXhrMap[url]);
        uniqueXhrMap[url] = newGuid;
    }

    params.config = function (xhr) {
        xhrMap[newGuid] = xhr;
        apiResponseTimeService.addXhr(newGuid, xhr);
    };

    let options = Object.assign({
        url: getRequestURL(path),
        headers: {},
        withCredentials: true
    }, params);

    if (navigationModel.getCurrentMenuID()) {
        options.headers['From'] = navigationModel.getCurrentMenuID();
    }

    return m.request(options)
        .catch((error) => {
            // Not session timeout error, re-throw for further handling
            if (error.code !== apiStatusCodes.unauthorized) throw error;

            // Skip retry if already attempting a token refresh to prevent infinite loop

            if (isRefreshing) {
                // Queue the request to be retried after refresh completes
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(() => retryRaw(path, params, authenticationRequired, abortable));
            }

            isRefreshing = true;

            return RefreshService.refresh()
                .then(() => {
                    // After refresh make request again
                    processQueue(null);
                    return retryRaw(path, params, authenticationRequired, abortable);
                })
                .catch((refreshError) => {
                    processQueue(refreshError);
                    // Refresh failed — session is dead, redirect to login
                    clearCredentials()
                    sessionStorage.removeItem('mcs_last_route');
                    sessionStorage.removeItem('mcs_agreementYN');
                    m.route.set('/login');
                    throw refreshError;
                })
                .finally(() => {
                    isRefreshing = false;
                });
        })
        .finally(() => {
            delete xhrMap[newGuid];
            apiResponseTimeService.process(newGuid);
            if (abortable) {
                delete uniqueXhrMap[newGuid];
            }
        });
};

const abortRequest = (guid) => {
    if (xhrMap[guid]) {
        xhrMap[guid].abort();
        delete xhrMap[guid];
    }
};

/**
 * The Promise-base model base object. 
 * Refer to `src\app\models\model-base.model.js` for the Callback-based. 
 */
const modelBaseV2 = {

    get: function (path, params = {}, abortable = false) {
        params ||= {};
        params.method = 'GET';
        return request(path, params, true, abortable);
    },

    getPublic: function (path, params = {}, abortable = false) {
        params ||= {};
        params.method = 'GET';
        return request(path, params, false, abortable);
    },

    post: function (path, params = {}, abortable = false) {
        params ||= {};
        params.method = 'POST';
        return request(path, params, true, abortable);
    },

    postPublic: function (path, params = {}, abortable = false) {
        params ||= {};
        params.method = 'POST';
        return request(path, params, false, abortable);
    },

    put: function (path, params = {}, abortable = false) {
        params ||= {};
        params.method = 'PUT';
        return request(path, params, true, abortable);
    },

    downloadBlob: function (data, fileName) {
        try {
            if (typeof data === 'string' || data instanceof String) {
                var link = document.createElement("a");
                link.setAttribute('href', data);
                link.setAttribute('target', "_blank");
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                if (navigator.msSaveBlob) {
                    navigator.msSaveBlob(data, fileName);
                } else {
                    var link = document.createElement('a');
                    if (link.download !== undefined) {
                        var url = URL.createObjectURL(data);
                        link.setAttribute('href', url);
                        link.setAttribute('download', fileName);
                        link.style.visibility = 'hidden';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                }
            }
        }
        catch (e) {
            throw e;
        }
    },

    postDownload: function (path, params = {}, fileName, abortable = false) {
        params ||= {};
        params.responseType = "blob";
        return this.post(path, params, abortable).then(data => {
            if (!data || data.size === 0) {
                throw { code: apiStatusCodes.noContent };
            }
            this.downloadBlob(data, fileName);
        });
    },

    getDownload: function (path, params = {}, fileName, abortable = false) {
        params ||= {};
        params.responseType = "blob";
        return this.get(path, params, abortable).then(data => this.downloadBlob(data, fileName));
    },

    getDownloadPost: function (path, params = {}, fileName, abortable = false) {
        params ||= {};
        params.responseType = "blob";
        return this.post(path, params, abortable).then(data => this.downloadBlob(data, fileName));
    },

    getDownloadPublic: function (path, params = {}, fileName, abortable = false) {
        params ||= {};
        params.method = 'GET';
        params.responseType = "blob";
        return request(path, params, false, abortable).then(data => this.downloadBlob(data, fileName));
    },

    // Renamed from getImageUrl()
    getFileUrl: function (fileId) {
        let params = { responseType: "blob", params: { fileId: fileId } };

        return this.get('/api/file/:fileId', params).then(data => {
            let urlCreator = window.URL || window.webkitURL;
            return urlCreator.createObjectURL(data); // imageUrl / fileUrl
        })
    },

    uploadfile: function (file, category, abortable = false) {
        let formData = new FormData();
        formData.append("File0", file, file.name);
        let params = { method: 'POST', body: formData, params: { category: category } };

        return request(`/api/file/:category`, params, true, abortable);
    },

    abortAllRequest: () => {
        Object.keys(xhrMap).forEach((key) => {
            abortRequest(key);
        });
    },

    toQueryString: function (obj, prefix) {
        let str = [];
        for (let p in obj) {
            let k = prefix ? prefix + "[" + p + "]" : p,
                v = obj[p];
            if (v != null) {
                if ((typeof v === "object" && !_.isEmpty(v))) { // if object or array and is not empty
                    // str.push(serialize(v, k));
                    str.push(encodeURIComponent(k) + "=" + JSON.stringify(v))
                } else if (typeof v === "string") { // If string
                    if (v.length > 0) { // has value
                        str.push(encodeURIComponent(k) + "=" + encodeURIComponent(v));
                    }
                } else { // accept everything else
                    str.push(encodeURIComponent(k) + "=" + encodeURIComponent(v));
                }

            }
        }

        return str.join("&");
    }

}

export default modelBaseV2;
